// Auto-answered intake questions.
//
// A question can declare that, for a given answer to an EARLIER question, it
// should not be asked at all and a fixed choice recorded on the patient's
// behalf:
//
//   {
//     title: "Would you like monthly recurring shipment ...",
//     type: "radio",
//     code: "insomnia-medication-autoship",
//     choices: [{ label: "Yes ..." }, { label: "No", silent: true }],
//     autoAnswer: {
//       whenCode: "insomnia-medication-continue-try-different",
//       whenChoiceLabel: "Try something different",
//       selectChoiceLabel: "No",
//     },
//   }
//
// The question keeps its slot in the questionnaire — answers are keyed by
// question INDEX (and persisted that way in localStorage), so removing it
// would shift every later question's answers and corrupt them the moment the
// patient navigated back. It is skipped on the way past instead, with its
// answer recorded, which also keeps the resume-position calculation (which
// reads the set of answered indexes) contiguous and correct.
//
// Recording a real answer rather than leaving a gap is deliberate: the intake a
// clinician reads should say the patient declined, not go silent.

/**
 * The index of the question carrying `code`, or -1.
 */
export function questionIndexByCode(questionnaire, code) {
  const questions = questionnaire?.questions || [];
  return questions.findIndex((q) => q && q.code === code);
}

/**
 * The label the patient selected for the question carrying `code`, or "".
 * Radio answers record the chosen choice's label on `selected`.
 */
export function selectedLabelForCode(questionnaire, answers, code) {
  const index = questionIndexByCode(questionnaire, code);
  if (index < 0) return "";
  return answers?.[index]?.selected || "";
}

/**
 * Whether the question at `index` should be skipped and auto-answered given the
 * answers so far. False for any question that declares no autoAnswer, and for a
 * malformed declaration (a code or choice label that doesn't exist) — a
 * mistyped rule must fall back to ASKING the patient, never to silently
 * recording an answer they didn't give.
 */
export function isAutoAnswered(questionnaire, answers, index) {
  const question = questionnaire?.questions?.[index];
  const rule = question?.autoAnswer;
  if (!rule?.whenCode || !rule?.whenChoiceLabel || !rule?.selectChoiceLabel) {
    return false;
  }
  if (autoAnswerChoiceIndex(question) < 0) {
    return false; // selectChoiceLabel doesn't match any choice — ask instead
  }
  return (
    selectedLabelForCode(questionnaire, answers, rule.whenCode) ===
    rule.whenChoiceLabel
  );
}

/**
 * The index of the choice an auto-answered question records, or -1.
 */
export function autoAnswerChoiceIndex(question) {
  const choices = question?.choices || [];
  return choices.findIndex(
    (c) => c && c.label === question?.autoAnswer?.selectChoiceLabel,
  );
}

/**
 * The answer object to record for an auto-answered question, in the same shape
 * a patient's own radio selection produces (see consultationQuestion's
 * handleRadioChange): `selectedIndex` is the choice index as a STRING, because
 * that is what a DOM value yields and what the rest of the flow compares
 * against. Returns null when the question isn't auto-answerable.
 */
export function autoAnswerFor(questionnaire, index) {
  const question = questionnaire?.questions?.[index];
  const choiceIndex = autoAnswerChoiceIndex(question);
  if (choiceIndex < 0) return null;

  return {
    question,
    selected: question.choices[choiceIndex].label,
    selectedIndex: String(choiceIndex),
    // Marks the answer as ours rather than the patient's, for anyone reading
    // the persisted intake who needs to tell the difference.
    autoAnswered: true,
  };
}

/**
 * Walk from `questionId` (1-based) in `direction` (+1 / -1) past every question
 * that should be auto-answered, collecting what to record on the way.
 *
 * Returns { questionId, autoAnswers: { [index]: answer } }. The returned id is
 * clamped to the questionnaire: walking forward past the end yields
 * questions.length + 1 (the flow's "submit" sentinel), and backward past the
 * start yields 1.
 */
export function skipAutoAnsweredQuestions(
  questionnaire,
  answers,
  questionId,
  direction = 1,
) {
  const total = questionnaire?.questions?.length || 0;
  const autoAnswers = {};
  let id = questionId;

  while (id >= 1 && id <= total) {
    const index = id - 1;
    if (!isAutoAnswered(questionnaire, { ...answers, ...autoAnswers }, index)) {
      break;
    }
    const answer = autoAnswerFor(questionnaire, index);
    if (answer) autoAnswers[index] = answer;
    id += direction;
  }

  // Never hand back an id before the first question; walking backwards off the
  // start means every earlier question was auto-answered, so the first is where
  // the patient belongs.
  if (id < 1) id = 1;

  return { questionId: id, autoAnswers };
}
